import { convertFlowToGuide, GuideValidationError } from '@/db/convertFlowToGuide';
import { currentFlowState } from '@/state';
import { useUnwrappedAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { AppFlowDao, GuideDao } from '@rock/db-admin';
import type { AppFlowParams, GuideContext } from '@rock/types';
import { useState } from 'react';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('usePublishGuide');
const guideDao = new GuideDao();
const appFlowDao = new AppFlowDao();

/**
 * Two-stage guide publishing: **Publish to Preview** converts the draft flow into a guide and writes it
 * to the admin-only preview slot (internal devices with preview mode on render it); **Publish to Live**
 * promotes the previewed guide to the live slot everyone reads. Publish to Live is only meaningful once a
 * preview exists (`stagePending`), so what goes live is what was validated on-device.
 */
export function usePublishGuide(params: AppFlowParams) {
    const [busy, setBusy] = useState(false);
    const flow = useUnwrappedAtomValue(currentFlowState(params));
    const spaceId = flow?.space;

    function contextFor(): GuideContext | undefined {
        if (!flow || !spaceId) return undefined;
        return flow.equipmentTypeId ? { type: 'equipment', typeId: flow.equipmentTypeId } : { type: 'space', spaceId };
    }

    function reportError(action: string, err: unknown) {
        if (err instanceof z.ZodError) {
            for (const issue of err.issues) {
                toast.error(`Validation Error: ${action}`, {
                    description: `${issue.path.join('.')}: ${issue.message}`,
                });
            }
        } else if (err instanceof GuideValidationError) {
            toast.error(`Guide Validation Error: ${action}`, { description: err.message });
        } else {
            toast.error(`Error: ${action}`, { description: 'An unexpected error occurred.' });
            log.error(`Error: ${action}`, { err });
        }
    }

    async function publishToPreview() {
        const context = contextFor();
        if (!flow || !context) return;
        setBusy(true);
        try {
            const guide = convertFlowToGuide(flow);
            await guideDao.setGuidePreview(context, guide);
            toast.success('Published to Preview');
            log.info('Guide published to preview', { spaceId, flowId: flow.id });
        } catch (err) {
            reportError('Publish to Preview', err);
        } finally {
            setBusy(false);
        }
    }

    async function publishToLive() {
        const context = contextFor();
        if (!flow || !context) return;
        setBusy(true);
        try {
            const preview = await guideDao.getGuidePreview(context);
            if (!preview) {
                toast.error('Publish to Preview first', {
                    description: 'A guide must be previewed before it can go live.',
                });
                return;
            }
            // Promote the validated preview to live (strips `_draft`, discards the mirror doc);
            // keep the published flow current for re-editing.
            await guideDao.publishGuidePreview(context);
            await appFlowDao.setPublishedFlow(spaceId!, flow.equipmentTypeId, flow);
            toast.success('Published to Live');
            log.info('Guide published to live', { spaceId, flowId: flow.id });
        } catch (err) {
            reportError('Publish to Live', err);
        } finally {
            setBusy(false);
        }
    }

    return { busy, publishToPreview, publishToLive };
}
